import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../api";
import { MarketTag } from "../../components/MarketTag";
import { MarketTypeFilter } from "../../components/MarketTypeFilter";
import { matchesMarketType } from "../../components/marketType";
import type { MarketTypeChoice } from "../../components/marketType";
import { SortableTable } from "../../components/SortableTable";
import { date, timeAgo } from "../../format";
import { Card, PageHead } from "../../shell/ui";

interface Row {
  symbol: string;
  timeframe: string;
  marketType: string;
  candleCount: number;
  minTime: number;
  maxTime: number;
}

/** What the hub hosts. Nothing to download — it is kept current for everyone. */
export function MarketDataPage() {
  const q = useQuery({
    queryKey: ["hub-inventory"],
    queryFn: () => api<{ inventory: Row[] }>("/candles/inventory"),
    refetchInterval: 60_000,
  });
  const [marketType, setMarketType] = useState<MarketTypeChoice>("all");
  const rows = (q.data?.inventory ?? []).filter((r) => matchesMarketType(marketType, r.marketType));
  return (
    <div className="stack">
      <PageHead title="Market data">
        <MarketTypeFilter value={marketType} onChange={setMarketType} stocks />
      </PageHead>
      <p className="muted">
        StrategyHive hosts one shared archive and keeps it current every hour — there is nothing to download.
        Missing a market you want? Ask through <strong>Feedback</strong>, top right.
      </p>
      <Card flush>
        <SortableTable
          exportName="market-data"
          rows={rows}
          loading={q.isPending}
          rowKey={(r) => `${r.symbol}|${r.timeframe}|${r.marketType}`}
          initialSort={{ key: "symbol", direction: "asc" }}
          columns={[
            {
              key: "symbol",
              label: "Symbol",
              render: (r) => <strong>{r.symbol}</strong>,
              sortValue: (r) => r.symbol,
              firstDirection: "asc",
            },
            {
              key: "type",
              label: "Market",
              render: (r) => (r.marketType === "spot" ? "Spot" : <MarketTag marketType={r.marketType} />),
              sortValue: (r) => r.marketType,
              firstDirection: "asc",
            },
            {
              key: "tf",
              label: "Timeframe",
              render: (r) => r.timeframe,
              sortValue: (r) => r.timeframe,
              firstDirection: "asc",
            },
            {
              key: "bars",
              label: "Bars",
              align: "right",
              render: (r) => r.candleCount.toLocaleString(),
              sortValue: (r) => r.candleCount,
            },
            {
              key: "from",
              label: "From",
              align: "right",
              render: (r) => date(r.minTime),
              sortValue: (r) => r.minTime,
            },
            {
              key: "to",
              label: "Newest bar",
              align: "right",
              render: (r) => timeAgo(r.maxTime),
              sortValue: (r) => r.maxTime,
            },
          ]}
        />
      </Card>
    </div>
  );
}
